import axios from 'axios';
import { API_CONFIG } from '@/lib/config/index.js';
import type { InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { authService } from '@/features/user/services/authService';
import { triggerAuthLogout } from '@/app/providers/authEvents';

// Authentication is now handled via HttpOnly cookies
// No need for localStorage token management

/**
 * Global logout handler for 401 errors
 * This ensures consistent logout behavior when session expires
 */
let isLoggingOut = false;

const handleGlobalLogout = async () => {
  // Prevent multiple simultaneous logout calls
  if (isLoggingOut) return;
  isLoggingOut = true;

  try {
    // Clear cookies via API (skip interceptor to avoid loop)
    // Use publicApiClient directly to avoid going through interceptor
    await authService.logout().catch(() => {
      // Ignore errors - logout should always proceed
    });
  } finally {
    // Trigger AuthProvider to update immediately (it will clear localStorage)
    triggerAuthLogout();
    isLoggingOut = false;
  }
};

/**
 * Creates a configured Axios instance.
 * @param baseURL The base URL for the API.
 */
const createApiClient = (baseURL: string) => {
  const client = axios.create({
    baseURL,
    timeout: API_CONFIG.timeout,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: API_CONFIG.withCredentials,
  });

  // Request interceptor
  client.interceptors.request.use(
    (config) => {
      // With HttpOnly cookies, authentication is handled automatically
      // No need to manually add Authorization headers
      // The Authorization cookie will be sent automatically with withCredentials: true
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor
  client.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      if (error.response) {
        const { status, config } = error.response;
        
        switch (status) {
          case 401:
            // Unauthorized (401) - Session expired or invalid
            // Skip logout and validate endpoints to avoid infinite loops
            if (config?.url && 
                !config.url.includes('/public-user/logout') && 
                !config.url.includes('/public-user/validate')) {
              // Trigger global logout routine
              await handleGlobalLogout();
            }
            break;
          case 403:
            // Forbidden (403). User does not have permission.
            break;
          case 404:
            // Not Found (404).
            break;
          case 500:
            // Internal Server Error (500). Please try again later.
            break;
          default:
            // An HTTP error occurred
        }
      } else if (error.request) {
        // Network Error: No response received.
        
        if (error.message.includes('CORS') || error.message.includes('cors')) {
          // CORS Error detected. Check your proxy configuration in vite.config.ts
          // Make sure your development server is running and proxy is configured correctly
        }
      } else {
        // An unexpected error occurred
      }
      return Promise.reject(error);
    }
  );

  return client;
};

const publicApiClientInstance = createApiClient(API_CONFIG.baseURL);

const privateApiClientInstance = createApiClient(API_CONFIG.baseURL);

  // --- API WRAPPERS ---
export const apiClient = {
  get: <T = unknown>(url: string, config?: InternalAxiosRequestConfig): Promise<T> =>
    privateApiClientInstance.get(url, config).then((response: AxiosResponse<T>) => response.data) as Promise<T>,

  post: <T = unknown>(url: string, data?: unknown, config?: InternalAxiosRequestConfig): Promise<T> =>
    privateApiClientInstance.post(url, data, config).then((response: AxiosResponse<T>) => response.data) as Promise<T>,

  put: <T = unknown>(url: string, data?: unknown, config?: InternalAxiosRequestConfig): Promise<T> =>
    privateApiClientInstance.put(url, data, config).then((response: AxiosResponse<T>) => response.data) as Promise<T>,

  delete: <T = unknown>(url: string, config?: InternalAxiosRequestConfig): Promise<T> =>
    privateApiClientInstance.delete(url, config).then((response: AxiosResponse<T>) => response.data) as Promise<T>,
};

export const publicApiClient = {
  get: <T = unknown>(url: string, config?: InternalAxiosRequestConfig): Promise<T> =>
    publicApiClientInstance.get(url, config).then((response: AxiosResponse<T>) => response.data) as Promise<T>,
  
  post: <T = unknown>(url: string, data?: unknown, config?: InternalAxiosRequestConfig): Promise<T> =>
    publicApiClientInstance.post(url, data, config).then((response: AxiosResponse<T>) => response.data) as Promise<T>,

  put: <T = unknown>(url: string, data?: unknown, config?: InternalAxiosRequestConfig): Promise<T> =>
    publicApiClientInstance.put(url, data, config).then((response: AxiosResponse<T>) => response.data) as Promise<T>,
};
